import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'

import { beginTotpSetup, disableTotp, enableTotp, getTotpStatus } from '../api/admin'
import { Button, Field, Input, Panel, describeError, ui, useToast } from '../components/ui/Ui'

/**
 * Two-factor enrolment for the operator's own account. Every decision that
 * moves money or changes what users see asks for a code from here, so this
 * is the first screen a new operator visits.
 */
export default function Security(): JSX.Element {
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const [code, setCode] = useState('')
  const queryClient = useQueryClient()
  const toast = useToast()
  const status = useQuery({ queryKey: ['totp'], queryFn: getTotpStatus })

  const begin = useMutation({
    mutationFn: beginTotpSetup,
    onSuccess: (result) => setSetup(result),
    onError: (error) => toast(describeError(error), true),
  })
  const enable = useMutation({
    mutationFn: () => enableTotp(code),
    onSuccess: () => {
      toast('Двухфакторная защита включена')
      setSetup(null)
      setCode('')
      void queryClient.invalidateQueries({ queryKey: ['totp'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (error) => toast(describeError(error), true),
  })
  const disable = useMutation({
    mutationFn: () => disableTotp(code),
    onSuccess: () => {
      toast('Двухфакторная защита выключена')
      setCode('')
      void queryClient.invalidateQueries({ queryKey: ['totp'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const enabled = status.data?.enabled ?? false

  return (
    <div style={{ maxWidth: 640 }}>
      <Panel>
        <div style={{ padding: 20 }} className={ui.stack}>
          <div>
            <p className={ui.sectionTitle}>Состояние</p>
            <p style={{ margin: '8px 0 0' }}>
              {status.isPending ? '…' : enabled ? <span className="status status-approved">включена</span> : <span className="status status-pending">не включена</span>}
            </p>
            <p className={ui.hint} style={{ marginTop: 8 }}>
              Одобрение и отправка выводов, изменение цифр на карточках мастеров, настроек, реестра и статусов пользователей требуют одноразовый код. Без включённой защиты эти действия недоступны.
            </p>
          </div>

          {!enabled && setup === null ? (
            <div>
              <Button variant="primary" loading={begin.isPending} onClick={() => begin.mutate()}>
                Настроить приложение-аутентификатор
              </Button>
            </div>
          ) : null}

          {setup !== null ? (
            <div className={ui.stack}>
              <p className={ui.sectionTitle}>Шаг 1 — отсканируйте код</p>
              <div style={{ background: '#fff', padding: 12, borderRadius: 10, width: 'fit-content' }}>
                <QRCodeSVG value={setup.otpauthUrl} size={176} />
              </div>
              <Field label="Или введите секрет вручную">
                <Input mono readOnly value={setup.secret} onFocus={(event) => event.currentTarget.select()} />
              </Field>
              <p className={ui.sectionTitle}>Шаг 2 — подтвердите кодом из приложения</p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <Field label="Код">
                  <Input mono inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} style={{ width: 140 }} />
                </Field>
                <Button variant="primary" disabled={code.length < 6} loading={enable.isPending} onClick={() => enable.mutate()}>
                  Включить
                </Button>
              </div>
            </div>
          ) : null}

          {enabled ? (
            <div className={ui.stack}>
              <p className={ui.sectionTitle}>Выключить</p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <Field label="Код из приложения">
                  <Input mono inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} style={{ width: 140 }} />
                </Field>
                <Button variant="danger" disabled={code.length < 6} loading={disable.isPending} onClick={() => disable.mutate()}>
                  Выключить защиту
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  )
}
